import {
  AttributeAssessmentId,
  AttributeEvidence,
  AttributeEvidenceId,
  ComplexityAssessmentId,
  CreateAttributeEvidence,
  UpdateAttributeEvidence,
} from "platformed-client/types";

import {platformedClient} from "../..";
import {invalidateQueries, q} from "../../../state";

/**
 * Create new attribute evidence
 */
export async function create(
  complexityAssessmentId: ComplexityAssessmentId,
  evidence: CreateAttributeEvidence,
): Promise<AttributeEvidenceId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/evidence",
    {path: {complexity_assessment_id: complexityAssessmentId}, body: evidence},
  );

  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.attributeEvidence.filter(complexityAssessmentId, evidence.attribute_assessment_id),
    q.tprm.attributeEvidenceDeleted.filter(complexityAssessmentId, evidence.attribute_assessment_id),
    // Evidence changes can reflow attribute values which feed into
    // the tier — keep list mins and rollup widget fresh.
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);

  return res;
}

/**
 * List active evidence for a specific attribute assessment
 */
export async function list(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
): Promise<AttributeEvidence[]> {
  const result = await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/attribute_assessment/{attribute_assessment_id}/evidence" as any,
    {path: {complexity_assessment_id: complexityAssessmentId, attribute_assessment_id: attributeAssessmentId}},
  );

  return result as AttributeEvidence[];
}

/**
 * List deleted evidence for a specific attribute assessment
 */
export async function listDeleted(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
): Promise<AttributeEvidence[]> {
  const result = await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/attribute_assessment/{attribute_assessment_id}/evidence/deleted" as any,
    {path: {complexity_assessment_id: complexityAssessmentId, attribute_assessment_id: attributeAssessmentId}},
  );

  return result as AttributeEvidence[];
}

/**
 * Delete attribute evidence by ID
 */
export async function deleteById(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
  attributeEvidenceId: AttributeEvidenceId,
): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/evidence/{attribute_evidence_id}",
    {path: {complexity_assessment_id: complexityAssessmentId, attribute_evidence_id: attributeEvidenceId}},
  );

  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.attributeEvidence.filter(complexityAssessmentId, attributeAssessmentId),
    q.tprm.attributeEvidenceDeleted.filter(complexityAssessmentId, attributeAssessmentId),
    // Evidence changes can reflow attribute values which feed into
    // the tier — keep list mins and rollup widget fresh.
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
}

/**
 * Restore deleted attribute evidence by ID
 */
export async function restore(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
  attributeEvidenceId: AttributeEvidenceId,
): Promise<void> {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/evidence/{attribute_evidence_id}/restore",
    {path: {complexity_assessment_id: complexityAssessmentId, attribute_evidence_id: attributeEvidenceId}},
  );

  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.attributeEvidence.filter(complexityAssessmentId, attributeAssessmentId),
    q.tprm.attributeEvidenceDeleted.filter(complexityAssessmentId, attributeAssessmentId),
    // Evidence changes can reflow attribute values which feed into
    // the tier — keep list mins and rollup widget fresh.
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
}

/**
 * Update attribute evidence by ID
 */
export async function update(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
  attributeEvidenceId: AttributeEvidenceId,
  evidence: UpdateAttributeEvidence,
): Promise<void> {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/evidence/{attribute_evidence_id}" as any,
    {
      path: {complexity_assessment_id: complexityAssessmentId, attribute_evidence_id: attributeEvidenceId},
      body: evidence,
    },
  );

  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.attributeEvidence.filter(complexityAssessmentId, attributeAssessmentId),
    q.tprm.attributeEvidenceDeleted.filter(complexityAssessmentId, attributeAssessmentId),
    // Evidence changes can reflow attribute values which feed into
    // the tier — keep list mins and rollup widget fresh.
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
}
